import { useTheme } from "@emotion/react";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Slide,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { forwardRef, useContext, useEffect, useState } from "react";
import useInvoicedOrders from "../../../hooks/useInvoicedOrders";
import { useInView } from "react-intersection-observer";
import { useDebounce } from "use-debounce";
import TextFieldDebounced from "../../../components/TextFieldDebounced";
import InfiniteScrollerTable from "../../../components/InfiniteScroller/InfiniteScrollerTable";
import InvoiceTableRow from "./InvoiceTableRow";
import ListFooter from "../../../components/InfiniteScroller/ListFooter";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";

const CreateInvoiceDialog = ({ open, onClose }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const { authToken } = useContext(AuthContext);
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [
    orders,
    onRefresh,
    refreshing,
    onLoadMore,
    loadingMore,
    total,
    hasMore,
  ] = useInvoicedOrders(authToken, 50, null, search, "pending");

  const [ref, inView, entry] = useInView({
    /* Optional options */
    threshold: 0.5, //Number between 0 and 1 indicating the percentage that should be visible before triggering. Can also be an array of numbers, to create multiple trigger points.
  });

  const [inViewDebounce] = useDebounce(inView, 1000);

  const handleClose = () => {
    onClose();
  };

  useEffect(() => {
    if (orders.length > 0 && inView) {
      console.log("Footer Element In View. Load More...");
      onLoadMore();
    }
  }, [inViewDebounce]);

  return (
    <Dialog
      fullScreen={false}
      fullWidth={true}
      open={open}
      onClose={() => {
        handleClose();
      }}
    >
      <DialogTitle>Create New Invoice</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
            alignItems: "center",
            marginTop: "16px",
            marginBottom: "16px",
          }}
        >
          <Box sx={{ marginLeft: "8px", flex: 1 }}>
            <TextFieldDebounced
              fullWidth
              variant="outlined"
              label="Search From Orders With Pending Invoice"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
            />
          </Box>
        </Box>
        <InfiniteScrollerTable
          TableContainerComponent={Box}
          TableStyle={{ minWidth: "600px" }}
          TableHead={() => (
            <TableHead>
              <TableRow>
                <TableCell>Order ID</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Credit Type</TableCell>
                <TableCell>Payment Status</TableCell>
                <TableCell>Date Approved</TableCell>
                <TableCell>$</TableCell>
              </TableRow>
            </TableHead>
          )}
          items={orders}
          renderRow={(item, index) => {
            return (
              <InvoiceTableRow
                key={item.id}
                order={item}
                onSelect={(order) => {
                  navigate(`/invoices/${order.id}`);
                }}
                isPendingQuote={true}
              />
            );
          }}
          onRefresh={onRefresh}
          onEndReached={onLoadMore}
          loading={refreshing || loadingMore}
          ListFooterComponent={() => {
            return <ListFooter hasMore={hasMore} onClick={onLoadMore} />;
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            handleClose();
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateInvoiceDialog;
